import { useCallback } from 'react'
import {
  deleteField,
  doc,
  setDoc,
  updateDoc,
} from 'firebase/firestore'
import { firestore } from '../firebase'
import { useAuth } from '../auth/AuthProvider'

/**
 * PWA-side watchlist writes. Schema mirrors the desktop
 * `FirebaseSyncService.syncWatchlistMovieUpdate` payload (see
 * `src/main/services/FirebaseSyncService.ts` around the queue flush):
 *
 *   users/{uid}/library/watchlists → {
 *     [watchlistId]: {
 *       name, description, color, isDefault, updatedAt,
 *       movies: {
 *         [movieKey]: {
 *           movieId, tmdbId, addedAt, position, isExternal,
 *           externalData: { title, year, posterPath, ... }
 *         }
 *       }
 *     }
 *   }
 *
 * For entries added from the PWA, we always have a TMDB id — there is
 * no local file on a phone — so we write `isExternal: 1` and stash the
 * essentials (title / year / posterPath) under externalData so the
 * desktop's inbound sync can hydrate its SQLite mirror without an
 * extra TMDB round-trip.
 */
export interface WatchlistAddMovieInput {
  tmdbId: number
  title: string
  year: number | null
  /**
   * TMDB relative path (e.g. `/abc123.jpg`). Consumers can render it
   * via the standard `https://image.tmdb.org/t/p/w500${path}` prefix.
   */
  posterPath: string | null
}

export function useWriteWatchlists() {
  const { user } = useAuth()

  const createWatchlist = useCallback(
    async (input: { name: string; description?: string; color?: string }) => {
      if (!user) return null
      const id = `pwa_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
      const ref = doc(firestore, `users/${user.uid}/library/watchlists`)
      const now = new Date().toISOString()
      const payload: Record<string, any> = {
        [id]: {
          name: input.name.trim() || 'New watchlist',
          description: input.description?.trim() || null,
          color: input.color ?? null,
          isDefault: false,
          movies: {},
          updatedAt: now,
        },
      }
      await setDoc(ref, payload, { merge: true })
      return id
    },
    [user]
  )

  const updateWatchlistMeta = useCallback(
    async (
      watchlistId: string,
      changes: { name?: string; description?: string | null; color?: string | null }
    ) => {
      if (!user) return
      const ref = doc(firestore, `users/${user.uid}/library/watchlists`)
      const now = new Date().toISOString()
      const payload: Record<string, any> = {
        [watchlistId]: {
          ...(changes.name !== undefined ? { name: changes.name } : {}),
          ...(changes.description !== undefined
            ? { description: changes.description }
            : {}),
          ...(changes.color !== undefined ? { color: changes.color } : {}),
          updatedAt: now,
        },
      }
      await setDoc(ref, payload, { merge: true })
    },
    [user]
  )

  const deleteWatchlist = useCallback(
    async (watchlistId: string) => {
      if (!user) return
      const ref = doc(firestore, `users/${user.uid}/library/watchlists`)
      await updateDoc(ref, { [watchlistId]: deleteField() })
    },
    [user]
  )

  const addMovieToWatchlist = useCallback(
    async (watchlistId: string, movie: WatchlistAddMovieInput) => {
      if (!user) return
      const ref = doc(firestore, `users/${user.uid}/library/watchlists`)
      const now = new Date().toISOString()
      const movieKey = String(movie.tmdbId)
      const payload: Record<string, any> = {
        [watchlistId]: {
          movies: {
            [movieKey]: {
              movieId: movieKey,
              tmdbId: movie.tmdbId,
              addedAt: now,
              position: 0,
              isExternal: 1,
              externalData: {
                title: movie.title,
                year: movie.year,
                posterPath: movie.posterPath,
              },
              updatedAt: now,
            },
          },
          updatedAt: now,
        },
      }
      await setDoc(ref, payload, { merge: true })
    },
    [user]
  )

  const removeMovieFromWatchlist = useCallback(
    async (watchlistId: string, tmdbId: number) => {
      if (!user) return
      const ref = doc(firestore, `users/${user.uid}/library/watchlists`)
      const movieKey = String(tmdbId)
      await updateDoc(ref, {
        [`${watchlistId}.movies.${movieKey}`]: deleteField(),
        [`${watchlistId}.updatedAt`]: new Date().toISOString(),
      })
    },
    [user]
  )

  return {
    createWatchlist,
    updateWatchlistMeta,
    deleteWatchlist,
    addMovieToWatchlist,
    removeMovieFromWatchlist,
  }
}
